import React, { useState, useEffect } from "react";
import { AiOutlineLock } from "react-icons/ai";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import DarkModeToggle from "./../../components/Common/DarkModeToggle";
import useAuthStore from "./../../stores/authStore";
import { useNavigate, useSearchParams } from "react-router-dom";

const ChangePassword = () => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [localError, setLocalError] = useState("");
    const [resetSuccess, setResetSuccess] = useState(false);
    const [token, setToken] = useState("");

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const {
        resetPassword, // You need to add this to your auth store
        loading,
        error,
        clearErrors
    } = useAuthStore();

    useEffect(() => {
        // Get token from URL parameters
        const urlToken = searchParams.get('token');
        if (urlToken) {
            setToken(urlToken);
        } else {
            // If no token, redirect to forgot password page
            navigate("/forgot-password");
        }

        // Clear previous errors when component mounts
        clearErrors();
    }, [searchParams, navigate, clearErrors]);

    const validatePasswords = () => {
        if (newPassword.length < 8) {
            setLocalError("Le mot de passe doit contenir au moins 8 caractères");
            return false;
        }

        if (newPassword !== confirmPassword) {
            setLocalError("Les mots de passe ne correspondent pas");
            return false;
        }

        setLocalError("");
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validatePasswords()) {
            return;
        }

        if (resetPassword) {
            // Use auth store method if available
            const result = await resetPassword(token, newPassword, confirmPassword);
            if (result.success) {
                setResetSuccess(true);
            }
        } else {
            // Fallback to direct API call
            try {
                const response = await fetch("https://app-int-001.quickcom.ma/api/auth/reset-password", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        token: token,
                        newPassword: newPassword,
                        confirmPassword: confirmPassword
                    }),
                });

                const data = await response.json();

                if (response.ok) {
                    setResetSuccess(true);
                } else {
                    setLocalError(data.message || "Une erreur est survenue");
                }
            } catch (err) {
                setLocalError("Erreur de connexion. Veuillez réessayer.");
            }
        }
    };

    const handleBack = () => {
        navigate("/login");
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-white to-[#E0F2F1] dark:from-gray-900 dark:to-gray-800 py-8">
            {/* Dark Mode Toggle */}
            <DarkModeToggle />

            {/* Logo */}
            <div className="mb-16 text-center">
                <img src="/quickcom-logo-text.png" alt="QuickCom" className="h-12 w-auto dark:filter dark:brightness-90" />
            </div>

            {/* Store Illustration */}
            <div className="mb-6 w-72 sm:w-96">
                <img
                    src="/login.png"
                    alt="QuickCom"
                    className="w-full h-auto max-h-90 dark:filter dark:brightness-90"
                />
            </div>

            {resetSuccess ? (
                <div className="text-center w-full max-w-md px-6 sm:px-0">
                    <h2 className="text-xl font-medium text-[#00897B] dark:text-primary-light mb-4">Mot de passe modifié</h2>
                    <p className="text-base text-gray-700 dark:text-gray-300 mb-8">
                        Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.
                    </p>
                    <button
                        onClick={handleBack}
                        className="w-full py-3 bg-[#4DB6AC] dark:bg-primary-dark text-white font-medium rounded-md hover:bg-[#00897B] dark:hover:bg-primary-darker transition-colors duration-200"
                    >
                        Se connecter
                    </button>
                </div>
            ) : (
                <>
                    {/* Title */}
                    <h2 className="text-xl font-medium text-[#00897B] dark:text-primary-light mb-4 text-center">
                        Nouveau mot de passe
                    </h2>

                    {/* Description */}
                    <p className="text-center text-base font-normal text-gray-700 dark:text-gray-300 mb-8">
                        Saisissez votre nouveau mot de passe<br />
                        et confirmez-le
                    </p>

                    {/* Change Password Form */}
                    <form
                        onSubmit={handleSubmit}
                        className="w-full max-w-md px-6 sm:px-0"
                    >
                        {/* New Password Input */}
                        <div className="mb-4">
                            <div className="relative">
                                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500">
                                    <AiOutlineLock className="w-5 h-5" />
                                </div>
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    id="newPassword"
                                    name="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Nouveau mot de passe"
                                    required
                                    className="w-full py-3.5 pl-12 pr-12 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[#4DB6AC] dark:focus:border-primary-light"
                                />
                                <button
                                    type="button"
                                    onClick={toggleNewPasswordVisibility}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    {showNewPassword ? (
                                        <AiOutlineEyeInvisible className="w-5 h-5" />
                                    ) : (
                                        <AiOutlineEye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password Input */}
                        <div className="mb-6">
                            <div className="relative">
                                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500">
                                    <AiOutlineLock className="w-5 h-5" />
                                </div>
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirmer le mot de passe"
                                    required
                                    className="w-full py-3.5 pl-12 pr-12 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-[#4DB6AC] dark:focus:border-primary-light"
                                />
                                <button
                                    type="button"
                                    onClick={toggleConfirmPasswordVisibility}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    {showConfirmPassword ? (
                                        <AiOutlineEyeInvisible className="w-5 h-5" />
                                    ) : (
                                        <AiOutlineEye className="w-5 h-5" />
                                    )}
                                </button>
                            </div>

                            {/* Error Messages */}
                            {(localError || error) && (
                                <p className="text-sm text-red-500 dark:text-red-400 mt-1 text-center">
                                    {localError || error}
                                </p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-[#4DB6AC] dark:bg-primary-dark text-white font-medium rounded-md hover:bg-[#00897B] dark:hover:bg-primary-darker transition-colors duration-200 disabled:opacity-70"
                        >
                            {loading ? "Modification en cours..." : "Modifier le mot de passe"}
                        </button>

                        {/* Return Button */}
                        <div className="mt-4 text-center">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="text-[#4DB6AC] dark:text-primary-light text-sm font-medium"
                            >
                                Retour à la connexion
                            </button>
                        </div>
                    </form>
                </>
            )}
        </div>
    );
};

export default ChangePassword;
